import { ColorMode } from '@color';
import { interpPaths, Path, path, Point } from '@geom';
import { interpLayout } from '@snorf';
import { expectDefined, fixedNumber, lerp, type Draft } from '@utils';
import type { NID } from '../graph/types';
import { Doc } from './Doc';
import type { NodeLayout } from './NodeLayout';

const INTERPOLATION_PATH_FLATNESS = 1.0;
const PREFIX = '🟢    doc';

// --- Animation ---
/** Creates an interpolation function between this Doc state and an end state. */
export function interpDoc(
  start: Doc,
  end: Doc,
  colorMode: ColorMode,
  opts?: { entryPoints?: Map<NID, Point>; logging?: boolean; snorfLogging?: boolean }
): (t: number) => Doc {
  const { entryPoints, logging, snorfLogging } = {
    logging: false,
    snorfLogging: false,
    ...opts
  };
  interface Mutator {
    f: (draft: Draft<Doc>, t: number) => void;
    message: string;
  }
  const mutators: Mutator[] = [];

  function addMutator(mutator: Mutator): void {
    mutators.push(mutator);
    if (logging) {
      console.log(`${PREFIX} add: ${mutator.message}`);
    }
  }

  /* ---------------------------  NODE INTERP  --------------------------- */
  const nodeUnion = start.graph.nodes.union(end.graph.nodes);
  for (const node of nodeUnion) {
    let startLayout: NodeLayout | undefined;
    let endLayout: NodeLayout | undefined;

    const nodeExistsInStart = start.graph.hasNode(node);
    const nodeExistsInEnd = end.graph.hasNode(node);

    if (nodeExistsInStart) {
      startLayout = start.layout.nodes.get(node);
    }
    if (nodeExistsInEnd) {
      endLayout = end.layout.nodes.get(node);
    }

    let startCenter = startLayout?.center;
    const endCenter = endLayout?.center;

    // --- Exiting node : fade-out ---------------------------------------
    if (startLayout && !endLayout) {
      endLayout = startLayout;
      addMutator({
        message: `fade-out node '${node}'`,
        f: (draft, t) => {
          const nodeLayout = draft.nodeLayout(node);
          const fadeOpacity = lerp(t, [1, 0]);
          nodeLayout.opacity = fadeOpacity;
        }
      });
    }
    // --- Entering node : fade-in / fly-in ------------------------------
    else if (!startLayout && endLayout) {
      // If an entry position is recorded, start there
      let entryPoint: Point | undefined;
      if (entryPoints) entryPoint = entryPoints.get(node);

      // Clone from end for starting position
      startLayout = endLayout;

      if (entryPoint) {
        // fly-in from entry point
        startCenter = entryPoint;
      } else {
        startCenter = endLayout.center; // no entry point, use end center
        // fade-in from final position
        // startLayout = produce(startLayout, draft => {
        //   draft.opacity = 0; // Start invisible
        // });
        addMutator({
          message: `fade-in node '${node}'`,
          f: (draft, t) => {
            const nodeLayout = draft.nodeLayout(node);
            const fadeOpacity = lerp(t, [0, 1]);
            nodeLayout.opacity = fadeOpacity;
          }
        });
      }
    }

    // --- Bounds interpolation -----------------------------------------
    if (startCenter && endCenter && !startCenter.equals(endCenter)) {
      const sc = startCenter;
      const ec = endCenter;
      addMutator({
        message: `move node '${node}'`,
        f: (draft, t) => {
          draft.nodeLayout(node).center = sc.lerp(t, ec);
        }
      });
    }

    const startSize = startLayout?.size;
    const endSize = endLayout?.size;

    // Determine if this node is SNORF-backed in both start and end (i.e., a unit)
    // But exclude entering/exiting nodes from SNORF interpolation since they handle opacity separately
    const startSnorfLayout = startLayout?.snorfLayout;
    const endSnorfLayout = endLayout?.snorfLayout;
    const isEnteringNode = !nodeExistsInStart && nodeExistsInEnd;
    const isExitingNode = nodeExistsInStart && !nodeExistsInEnd;
    const hasSnorfInBoth =
      Boolean(startSnorfLayout && endSnorfLayout) && !isEnteringNode && !isExitingNode;

    // --- Size interpolation -----------------------------------------
    // Units (SNORF-backed) do their own size interpolation via SNORF; keep doc-level size interp for groups only.
    if (!hasSnorfInBoth && startSize && endSize && !startSize.equals(endSize)) {
      const ss = startSize;
      const es = endSize;
      addMutator({
        message: `resize node '${node}'`,
        f: (draft, t) => {
          draft.nodeLayout(node).size = ss.lerp(t, es);
        }
      });
    }

    // --- Content interpolation (SNORF) -------------------------------
    if (hasSnorfInBoth) {
      const layoutInterp = interpLayout(
        expectDefined(startSnorfLayout),
        expectDefined(endSnorfLayout),
        colorMode,
        { logging: snorfLogging }
      );
      addMutator({
        message: `layoutInterp node '${node}'`,
        f: (draft, t) => {
          const nl = draft.nodeLayout(node);
          const snLayout = layoutInterp(t);
          nl.snorfLayout = snLayout;
          nl.size = snLayout.size;
        }
      });
    }
  }

  /* ---------------------------  EDGE INTERP  --------------------------- */
  const edgeUnion = start.graph.edges.union(end.graph.edges);
  for (const edge of edgeUnion) {
    let startPath: Path | undefined;
    let endPath: Path | undefined;

    if (start.graph.hasEdge(edge)) {
      startPath = start.edgeLayout(edge).path;
    }
    if (end.graph.hasEdge(edge)) {
      endPath = end.edgeLayout(edge).path;
    }

    // --- Exiting edge ---------------------------------------------------
    if (startPath && !endPath) {
      endPath = path().moveTo(startPath.start).lineTo(startPath.start).build();
      addMutator({
        message: `fade-out edge '${edge}'`,
        f: (draft, t) => {
          draft.edgeLayout(edge).opacity = lerp(t, [1, 0]);
        }
      });
    }

    // --- Entering edge --------------------------------------------------
    else if (!startPath && endPath) {
      startPath = path().moveTo(endPath.start).lineTo(endPath.start).build();
      addMutator({
        message: `fade-in edge '${edge}'`,
        f: (draft, t) => {
          draft.edgeLayout(edge).opacity = lerp(t, [0, 1]);
        }
      });
    }

    // --- Path interpolation --------------------------------------------
    if (startPath && endPath && !startPath.equals(endPath)) {
      const interp = interpPaths(startPath, endPath, INTERPOLATION_PATH_FLATNESS);
      addMutator({
        message: `pathInterp edge '${edge}'`,
        f: (draft, t) => {
          const p = interp(t);
          // force length cache eval
          const _ = p.length;
          draft.edgeLayout(edge).path = p;
        }
      });
    }
  }

  /* ---------------------------  MIDDLE STATE  -------------------------- */
  const middle = start.produce('interp middle', draft => {
    // Ensure all nodes/edges exist in draft
    for (const node of nodeUnion) {
      if (!start.graph.hasNode(node)) {
        const spec = end.nodeSpec(node);
        draft.graph.addNode(node, { data: spec });
        const layout = expectDefined(end.layout.nodes.get(node));
        draft.layout.nodes.set(node, layout);
      }
    }
    for (const edge of edgeUnion) {
      if (!start.graph.hasEdge(edge)) {
        const spec = end.edgeSpec(edge);
        draft.graph.addEdge(edge, end.graph.source(edge), end.graph.target(edge), spec);
        const layout = expectDefined(end.layout.edges.get(edge));
        draft.layout.edges.set(edge, layout);
      }
    }
  });

  /* ---------------------------  RETURN FUNC  --------------------------- */
  return (t: number): Doc => {
    if (logging) {
      console.log(`${PREFIX} at t=${fixedNumber(t, 2)}`);
    }

    if (t >= 1) {
      if (logging) {
        console.log(`${PREFIX} at t=${fixedNumber(t, 2)}: end`);
      }
      return end;
    }

    let nextState: Doc;
    if (logging) {
      const [_nextState, patches] = middle.produceWithPatches(
        `interp ${fixedNumber(t, 2)}`,
        draft => {
          for (const mutator of mutators) {
            console.log(`${PREFIX} run: ${mutator.message}`);
            mutator.f(draft, t);
          }
        }
      );
      console.log(`${PREFIX} patches:`, patches);
      nextState = _nextState;
    } else {
      nextState = middle.produce(`interp ${fixedNumber(t, 2)}`, draft => {
        for (const mutator of mutators) mutator.f(draft, t);
      });
    }
    return nextState;
  };
}
